import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { Mail, ArrowLeft } from 'lucide-react'

interface EmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentEmail: string
  emailVerified: boolean
  pendingEmail: string | null
  pendingEmailInput: string
  onPendingEmailChange: (value: string) => void
  onSendVerification: () => Promise<void>
  onResend: () => void
  onVerifyCode: (code: string) => void
  emailCode: string
  onEmailCodeChange: (value: string) => void
  isSending: boolean
  isResending: boolean
  isVerifying: boolean
}

export function EmailDialog({
  open,
  onOpenChange,
  currentEmail,
  emailVerified,
  pendingEmail,
  pendingEmailInput,
  onPendingEmailChange,
  onSendVerification,
  onResend,
  onVerifyCode,
  emailCode,
  onEmailCodeChange,
  isSending,
  isResending,
  isVerifying,
}: EmailDialogProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<'step1' | 'step2'>('step1')

  // Sync step with pendingEmail when dialog opens or pendingEmail changes externally
  useEffect(() => {
    if (open) {
      setStep(pendingEmail ? 'step2' : 'step1')
    }
  }, [open, pendingEmail])

  // Reset code when moving back to step 1
  const handleBack = () => {
    setStep('step1')
    onEmailCodeChange('')
  }

  const handleSend = async () => {
    try {
      await onSendVerification()
      setStep('step2')
    } catch {
      // Error toast is already shown by the parent mutation's onError
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === 'step1'
              ? t('settings.dialogs.email.step1Title')
              : t('settings.dialogs.email.step2Title')}
          </DialogTitle>
          <DialogDescription>
            {step === 'step1'
              ? t('settings.dialogs.email.step1Description')
              : t('settings.dialogs.email.step2Description')}
          </DialogDescription>
        </DialogHeader>

        {step === 'step1' ? (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (pendingEmailInput.trim().length > 0) handleSend()
            }}
            className="flex flex-col gap-4 py-2"
          >
            <div className="rounded-lg bg-muted/30 p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('settings.dialogs.email.currentEmail')}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-foreground">{currentEmail}</p>
                <Badge variant={emailVerified ? 'default' : 'outline'} size="sm">
                  {emailVerified
                    ? t('settings.dialogs.email.verified')
                    : t('settings.dialogs.email.verificationPending')}
                </Badge>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" htmlFor="settings-email-input">
                {t('settings.dialogs.email.newEmail')}
              </label>
              <Input
                id="settings-email-input"
                type="email"
                value={pendingEmailInput}
                onChange={(e) => onPendingEmailChange(e.target.value)}
                placeholder={t('settings.dialogs.email.placeholder')}
                autoComplete="email"
              />
              <p className="text-xs text-muted-foreground">
                {t('settings.dialogs.email.newEmailHint')}
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isSending || pendingEmailInput.trim().length === 0}
              >
                {isSending ? (
                  t('settings.dialogs.email.sending')
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="size-4" />
                    {t('settings.dialogs.email.sendVerification')}
                  </span>
                )}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex flex-col gap-5 py-2">
            <div className="rounded-lg bg-muted/30 p-4">
              <p className="text-sm text-muted-foreground">
                {t('settings.dialogs.email.verificationCodeSent', {
                  email: pendingEmail || pendingEmailInput.trim(),
                })}
              </p>
            </div>

            <div className="flex flex-col items-center gap-4">
              <label className="text-sm font-medium">
                {t('settings.dialogs.email.codeLabel')}
              </label>
              <InputOTP
                maxLength={6}
                value={emailCode}
                onChange={onEmailCodeChange}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>

              <Button
                type="button"
                variant="link"
                size="sm"
                disabled={isResending}
                onClick={onResend}
                className="h-auto py-0 text-muted-foreground hover:text-foreground"
              >
                {isResending
                  ? t('settings.dialogs.email.resending')
                  : t('settings.dialogs.email.resendLinkAndCode')}
              </Button>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-1 size-4" />
                {t('settings.dialogs.email.back')}
              </Button>
              <Button
                type="button"
                disabled={isVerifying || emailCode.length !== 6}
                onClick={() => onVerifyCode(emailCode)}
              >
                {isVerifying
                  ? t('settings.dialogs.email.verifying')
                  : t('settings.dialogs.email.verifyWithCode')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
